import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  BALANCE_QUERY_KEY,
  CREDITS_QUERY_KEY,
  TRANSACTIONS_PREFIX,
  useBalance,
  useDisplayRate,
  useTopupStatus,
} from '../api/useBilling.ts';
import { Skeleton } from '../components/Skeleton.tsx';
import {
  formatCnyCents,
  formatUsdCents,
  formatUsdCentsAsCny,
} from '../components/formatters.ts';
import { useT } from '../i18n/index.ts';
import { PageHeader, Panel, StatusLamp, buttonClass } from '../ui/index.ts';
import type { LampTone } from '../ui/index.ts';

/**
 * `/billing/topup/return` — landing page after ZPay's `return_url`
 * redirect (epic #950 PR-3, local Field Notes rebuild of the
 * console-shared `createBillingTopupReturnPage` factory instantiation).
 *
 * Wire behavior preserved: `order_id` is the source of truth and the
 * page polls `GET /v1/billing/topup/:order_id`; after
 * `PENDING_CEILING_MS` of continuous pending it switches to slow mode
 * and stops polling; `paid` invalidates balance + transactions and
 * auto-redirects to `/billing` after `PAID_REDIRECT_MS`; expired /
 * failed / refunded land on terminal panels with a retry CTA.
 */

/**
 * After this many ms of continuous `pending` the return page stops
 * polling and shows the slow-mode panel. Exported so tests can
 * reference the exact boundary value without re-hardcoding it.
 */
export const PENDING_CEILING_MS = 60_000;

/** Auto-redirect delay on the `paid` success panel. */
export const PAID_REDIRECT_MS = 5_000;

type ViewState =
  | { readonly kind: 'missing-order' }
  | { readonly kind: 'loading' }
  | { readonly kind: 'pending' }
  | { readonly kind: 'slow-pending' }
  | { readonly kind: 'paid' }
  | { readonly kind: 'expired' }
  | { readonly kind: 'failed' }
  | { readonly kind: 'refunded' };

export function BillingTopupReturnPage(): JSX.Element {
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const t = useT();

  const orderId = search.get('order_id');
  const [slowMode, setSlowMode] = useState(false);
  const mountTsRef = useRef<number>(Date.now());

  const statusQ = useTopupStatus(orderId, { enabled: !slowMode });
  const balanceQ = useBalance();
  const displayRateQ = useDisplayRate();

  // Switch to slow-mode after the ceiling elapses with the order still
  // pending. One fixed-time ceiling captured at mount — the effect does
  // not re-arm on re-renders.
  useEffect(() => {
    if (orderId === null || orderId.length === 0) {
      return;
    }
    const elapsed = Date.now() - mountTsRef.current;
    const remaining = Math.max(0, PENDING_CEILING_MS - elapsed);
    const handle = setTimeout(() => {
      setSlowMode(true);
    }, remaining);
    return () => clearTimeout(handle);
  }, [orderId]);

  const view: ViewState = (() => {
    if (orderId === null || orderId.length === 0) {
      return { kind: 'missing-order' };
    }
    if (statusQ.isLoading || statusQ.data === undefined) {
      return { kind: 'loading' };
    }
    const status = statusQ.data.status;
    if (status === 'paid') {
      return { kind: 'paid' };
    }
    if (status === 'expired') {
      return { kind: 'expired' };
    }
    if (status === 'failed') {
      return { kind: 'failed' };
    }
    if (status === 'refunded') {
      return { kind: 'refunded' };
    }
    return slowMode ? { kind: 'slow-pending' } : { kind: 'pending' };
  })();

  // Invalidate balance + transactions the moment we see `paid` so the
  // /billing view behind this page reflects the new credit.
  const paidInvalidatedRef = useRef(false);
  useEffect(() => {
    if (view.kind !== 'paid' || paidInvalidatedRef.current) {
      return;
    }
    paidInvalidatedRef.current = true;
    void qc.invalidateQueries({ queryKey: BALANCE_QUERY_KEY });
    void qc.invalidateQueries({ queryKey: CREDITS_QUERY_KEY });
    void qc.invalidateQueries({ queryKey: TRANSACTIONS_PREFIX });
  }, [view.kind, qc]);

  // Auto-redirect to /billing after the configured delay on a paid
  // order. The CTA gives the user a manual escape hatch.
  useEffect(() => {
    if (view.kind !== 'paid') {
      return;
    }
    const handle = setTimeout(() => {
      navigate('/billing', { replace: true });
    }, PAID_REDIRECT_MS);
    return () => clearTimeout(handle);
  }, [view.kind, navigate]);

  return (
    <section data-testid="billing-topup-return-page" className="flex flex-col gap-4">
      <PageHeader
        title={t('billing.topup_return.page.title')}
        sub={
          orderId !== null && orderId.length > 0 ? (
            <>
              {t('billing.topup_return.page.order_label')}
              <span className="font-mono text-ink-mid">{orderId}</span>
            </>
          ) : (
            t('billing.topup_return.page.waiting_order_id')
          )
        }
        testId="billing-topup-return-header"
      />

      {view.kind === 'missing-order' ? <MissingOrderPanel /> : null}
      {view.kind === 'loading' ? <LoadingPanel /> : null}
      {view.kind === 'pending' ? <PendingPanel /> : null}
      {view.kind === 'slow-pending' ? <SlowPendingPanel orderId={orderId ?? ''} /> : null}
      {view.kind === 'paid' ? (
        <PaidPanel
          amountUsdCents={statusQ.data?.amountUsdCents ?? 0}
          appliedRate={statusQ.data?.appliedRate ?? null}
          amountCnyCents={statusQ.data?.amountCnyCents ?? 0}
          newBalanceUsdCents={balanceQ.data?.balanceUsdCents}
          newBalanceLoading={balanceQ.isLoading}
          cnyPerUsd={displayRateQ.data?.cnyPerUsd ?? null}
        />
      ) : null}
      {view.kind === 'expired' ? <TerminalErrorPanel kind="expired" /> : null}
      {view.kind === 'failed' ? <TerminalErrorPanel kind="failed" /> : null}
      {view.kind === 'refunded' ? <TerminalErrorPanel kind="refunded" /> : null}
    </section>
  );
}

function lampCaption(tone: LampTone, pulse: boolean, text: ReactNode): ReactNode {
  return (
    <span className="inline-flex items-center gap-2">
      <StatusLamp tone={tone} pulse={pulse} />
      {text}
    </span>
  );
}

function LoadingPanel(): JSX.Element {
  const t = useT();
  return (
    <div className="flex flex-col gap-2" data-testid="billing-topup-return-loading">
      <Skeleton className="h-10 w-48" />
      <Skeleton className="h-4 w-64" />
      <span className="sr-only">{t('billing.topup_return.loading.aria')}</span>
    </div>
  );
}

function MissingOrderPanel(): JSX.Element {
  const t = useT();
  return (
    <Panel
      caption={lampCaption('warn', false, t('billing.topup_return.missing.title'))}
      padded
      testId="billing-topup-return-missing"
      className="max-w-2xl"
    >
      <div className="flex flex-col gap-4">
        <p className="m-0 text-body text-ink-mid">{t('billing.topup_return.missing.body')}</p>
        <Link to="/billing" className={`${buttonClass('primary')} w-fit`}>
          {t('billing.topup_return.missing.cta')}
        </Link>
      </div>
    </Panel>
  );
}

function PendingPanel(): JSX.Element {
  const t = useT();
  return (
    <Panel
      caption={lampCaption('rust', true, t('billing.topup_return.pending.title'))}
      padded
      testId="billing-topup-return-pending"
      className="max-w-2xl"
    >
      <p role="status" className="m-0 text-body text-ink-mid">
        {t('billing.topup_return.pending.body')}
      </p>
    </Panel>
  );
}

function SlowPendingPanel({ orderId }: { readonly orderId: string }): JSX.Element {
  const t = useT();
  return (
    <Panel
      caption={lampCaption('warn', false, t('billing.topup_return.slow.title'))}
      padded
      testId="billing-topup-return-slow"
      className="max-w-2xl"
    >
      <div className="flex flex-col gap-4">
        <p className="m-0 text-body text-ink-mid">{t('billing.topup_return.slow.body')}</p>
        <p className="m-0 text-label text-ink-low">
          {t('billing.topup_return.slow.reference')}{' '}
          <span className="font-mono text-ink-mid">{orderId}</span>
        </p>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <Link to="/billing" className={buttonClass('secondary')}>
            {t('billing.topup_return.slow.cta.back')}
          </Link>
          <a
            href="mailto:support@nxtpath.ai"
            className={buttonClass('primary')}
            data-testid="billing-topup-return-support"
          >
            {t('billing.topup_return.slow.cta.support')}
          </a>
        </div>
      </div>
    </Panel>
  );
}

interface PaidPanelProps {
  readonly amountUsdCents: number;
  readonly amountCnyCents: number;
  readonly appliedRate: number | null;
  readonly newBalanceUsdCents: number | undefined;
  readonly newBalanceLoading: boolean;
  readonly cnyPerUsd: number | null;
}

function PaidPanel({
  amountUsdCents,
  amountCnyCents,
  appliedRate,
  newBalanceUsdCents,
  newBalanceLoading,
  cnyPerUsd,
}: PaidPanelProps): JSX.Element {
  const t = useT();
  return (
    <Panel
      caption={lampCaption('ok', false, t('billing.topup_return.paid.title'))}
      padded
      testId="billing-topup-return-paid"
      className="max-w-2xl"
    >
      <div role="status" className="flex flex-col gap-4">
        <dl className="m-0 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="flex flex-col gap-1">
            <dt className="text-label text-ink-low">{t('billing.topup_return.paid.credited')}</dt>
            <dd
              className="m-0 font-mono text-title text-ok"
              data-testid="billing-topup-return-credited"
            >
              {formatCnyCents(amountCnyCents, true)}
            </dd>
          </div>
          <div className="flex flex-col gap-1">
            <dt className="text-label text-ink-low">{t('billing.topup_return.paid.charged')}</dt>
            <dd
              className="m-0 font-mono text-body text-ink-mid"
              data-testid="billing-topup-return-charged-cny"
            >
              {formatCnyCents(amountCnyCents)}
              {appliedRate !== null ? (
                <span className="ml-2 text-mono-label text-ink-low">
                  {t('billing.topup_return.paid.at_rate', { rate: appliedRate.toFixed(2) })}
                </span>
              ) : null}
            </dd>
          </div>
          <div className="flex flex-col gap-1 sm:col-span-2">
            <dt className="text-label text-ink-low">
              {t('billing.topup_return.paid.new_balance')}
            </dt>
            <dd
              className="m-0 font-mono text-stat text-ink"
              data-testid="billing-topup-return-balance"
            >
              {newBalanceLoading
                ? t('billing.topup_return.paid.balance_loading')
                : newBalanceUsdCents !== undefined
                  ? formatUsdCentsAsCny(newBalanceUsdCents, cnyPerUsd)
                  : '—'}
            </dd>
          </div>
        </dl>

        <details className="rounded-lg border border-line-soft bg-raised px-3 py-2 text-caption text-ink-low">
          <summary className="cursor-pointer text-label text-ink-mid">
            {t('billing.topup_return.paid.technical')}
          </summary>
          <dl className="m-0 mt-2 grid gap-2 sm:grid-cols-2">
            <div>
              <dt className="font-mono text-mono-label text-ink-low">USD credit</dt>
              <dd className="m-0 font-mono text-body text-ink-mid">
                {formatUsdCents(amountUsdCents, true)}
              </dd>
            </div>
            {newBalanceUsdCents !== undefined ? (
              <div>
                <dt className="font-mono text-mono-label text-ink-low">USD ledger balance</dt>
                <dd className="m-0 font-mono text-body text-ink-mid">
                  {formatUsdCents(newBalanceUsdCents)}
                </dd>
              </div>
            ) : null}
          </dl>
        </details>

        <p className="m-0 text-label text-ink-low">
          {t('billing.topup_return.paid.redirect_hint')}
        </p>
        <Link
          to="/billing"
          className={`${buttonClass('primary')} w-fit`}
          data-testid="billing-topup-return-cta"
        >
          {t('billing.topup_return.paid.cta')}
        </Link>
      </div>
    </Panel>
  );
}

interface TerminalErrorPanelProps {
  readonly kind: 'expired' | 'failed' | 'refunded';
}

function TerminalErrorPanel({ kind }: TerminalErrorPanelProps): JSX.Element {
  const t = useT();
  const headline =
    kind === 'expired'
      ? t('billing.topup_return.terminal.expired.title')
      : kind === 'failed'
        ? t('billing.topup_return.terminal.failed.title')
        : t('billing.topup_return.terminal.refunded.title');
  const body =
    kind === 'expired'
      ? t('billing.topup_return.terminal.expired.body')
      : kind === 'failed'
        ? t('billing.topup_return.terminal.failed.body')
        : t('billing.topup_return.terminal.refunded.body');
  return (
    <Panel
      caption={
        <span className="inline-flex items-center gap-2 text-bad">
          <StatusLamp tone="bad" />
          {headline}
        </span>
      }
      padded
      testId={`billing-topup-return-${kind}`}
      className="max-w-2xl"
    >
      <div role="alert" className="flex flex-col gap-4">
        <p className="m-0 text-body text-ink-mid">{body}</p>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <Link
            to="/billing/topup"
            className={buttonClass('primary')}
            data-testid="billing-topup-return-retry"
          >
            {t('billing.topup_return.terminal.cta.retry')}
          </Link>
          <Link to="/billing" className={buttonClass('ghost')}>
            {t('billing.topup_return.terminal.cta.back')}
          </Link>
        </div>
      </div>
    </Panel>
  );
}
